import React, { useEffect, useState, useRef } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  SelectChangeEvent,
  Stack,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Close as CloseIcon,
} from "../../../common/utils/mui-imports";
import HPRCheckBox from "../HPRCheckBox/HPRCheckBox";
import { ArrowDown2, SearchNormal1 } from "iconsax-react";
import { MultipleSelectorWithSearchProps } from "./MultipleSelectorWithSearch.types";
import { DropDownDTO } from "../../../common/types/common.dto.types";

// Custom Triangle Icon Component
const TriangleIcon = (
  { className }: { className?: string },
  disbaled: boolean
) => (
  <ArrowDown2
    style={{ marginRight: "18px" }}
    className={className}
    size="18"
    color={disbaled ? "#808080" : "#219EBC"}
    //non disabled color #219EBC
    variant="Bold"
  />
);

const MultipleSelectorWithSearch: React.FC<MultipleSelectorWithSearchProps> = (
  props: MultipleSelectorWithSearchProps
) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const [options, setOptions] = useState<Array<DropDownDTO>>([]);
  const [allOptions, setAllOptions] = useState<Array<DropDownDTO>>([]);
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectWidth, setSelectWidth] = useState<number>(0);
  const [openUpward, setOpenUpward] = useState<boolean>(false);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);

  // Calculate dropdown position based on available space
  const calculateDropdownPosition = () => {
    if (!selectRef.current) return false;

    const selectRect = selectRef.current?.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - selectRect.bottom;
    const spaceAbove = selectRect.top;

    // Estimate dropdown height based on options count
    // Assuming each option is approximately 48px + header/search area (~120px)
    const estimatedDropdownHeight = Math.min(
      120 + (options?.length || 0) * 48,
      300 // maxHeight from our CSS
    );

    // Check if there's enough space below
    const hasSpaceBelow = spaceBelow >= estimatedDropdownHeight + 20; // 20px buffer

    return !hasSpaceBelow && spaceAbove > estimatedDropdownHeight;
  };

  // Real-time width calculation using ResizeObserver
  useEffect(() => {
    if (!selectRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setSelectWidth(width);
      }
    });

    resizeObserver.observe(selectRef.current);

    // Cleanup observer on unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Alternative method using window resize listener (fallback)
  useEffect(() => {
    const updateWidth = () => {
      if (selectRef.current) {
        setSelectWidth(selectRef.current.offsetWidth);
      }
    };

    // Initial width calculation
    updateWidth();

    // Listen for window resize events
    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  useEffect(() => {
    setSelectedItems(props.SelectedValueArray);
  }, [props.SelectedValueArray]);

  useEffect(() => {
    // Only initialize allOptions when props.Options change
    setAllOptions(props.Options);

    // Only set options to all options if no search has been performed
    if (!searchPerformed) {
      setOptions(props.Options);
    }
  }, [props.Options]);
  // const handleSelectionChange = (event: SelectChangeEvent<string[]>) => {
  //   const value = event.target.value as string[];
  //   setSelectedItems(value);
  //   props.onSelectionChange?.(value);
  // };

  // Recalculate position when options change
  useEffect(() => {
    if (isOpen) {
      const shouldOpenUpward = calculateDropdownPosition();
      setOpenUpward(shouldOpenUpward);
    }
  }, [options, isOpen]);

  const handleSelectAll = () => {
    setSelectedItems(allOptions?.map((option) => option.Value) || []);
    props.onSelectionChange?.(options.map((option) => option.Value));
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
    props.onSelectionChange?.([]);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // If search term becomes empty, show all options immediately
    if (value.trim() === "") {
      setSearchPerformed(false);
      setOptions(allOptions || []);
    }
  };

  const handleMouseEnter = () => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchPerformed(false);
    // Reset to show all options when clearing search
    setOptions(allOptions || []);
  };

  const handleSearch = () => {
    setSearchPerformed(true);
    if (searchTerm.trim() === "") {
      setOptions(allOptions || []);
    } else {
      const filtered = allOptions?.filter((option) =>
        option.Label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setOptions(filtered || []);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    // Update width when dropdown opens
    if (selectRef.current) {
      setSelectWidth(selectRef.current.offsetWidth);
    }

    // Calculate dropdown position
    setTimeout(() => {
      const shouldOpenUpward = calculateDropdownPosition();
      setOpenUpward(shouldOpenUpward);
    }, 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
    setSearchPerformed(false);
    setOpenUpward(false);
    // Reset options to show all when closing
    setOptions(allOptions || []);
  };

  const onKeyPressSearch = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleCheckboxSelection = (selectedValue: string, value: boolean) => {
    if (value) {
      //setSelectedItems((prev) => [...prev, selectedValue]);
      props.onSelectionChange?.([...selectedItems, selectedValue]);
    } else {
      //setSelectedItems((prev) => prev.filter((item) => item !== selectedValue));
      props.onSelectionChange?.(
        selectedItems.filter((item) => item !== selectedValue)
      );
    }
  };

  // Enhanced handler for full row click
  const handleOptionClick = (optionValue: string, event: React.MouseEvent) => {
    // Prevent the default MenuItem behavior
    event.preventDefault();
    event.stopPropagation();

    // Toggle the checkbox state
    const isCurrentlySelected = selectedItems.includes(optionValue);
    handleCheckboxSelection(optionValue, !isCurrentlySelected);
  };

  // Handle keyboard events to prevent MUI Select's default keyboard navigation
  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    // Stop propagation to prevent Select's keyboard navigation
    event.stopPropagation();

    // Handle Enter key for search
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  // Dynamic MenuProps based on available space
  const getMenuProps = () => {
    const baseProps = {
      PaperProps: {
        className: "ms-menu-paper",
        style: {
          width: selectWidth,
          minWidth: selectWidth,
          maxHeight: "300px",
        },
      },
      autoFocus: false,
      disableAutoFocusItem: true,
      marginThreshold: 0,
    };

    if (openUpward) {
      // Open above the select
      return {
        ...baseProps,
        anchorOrigin: {
          vertical: "top" as const,
          horizontal: "left" as const,
        },
        transformOrigin: {
          vertical: "bottom" as const,
          horizontal: "left" as const,
        },
        style: {
          marginTop: "-10px",
        },
      };
    } else {
      // Open below the select (default behavior)
      return {
        ...baseProps,
        anchorOrigin: {
          vertical: "bottom" as const,
          horizontal: "left" as const,
        },
        transformOrigin: {
          vertical: "top" as const,
          horizontal: "left" as const,
        },
      };
    }
  };

  const getSelectedValue = (): string[] | string => {
    const isMultiple = !(props.DisableMultipleSelect ?? false);
    const items = selectedItems ?? [];

    if (isMultiple) {
      return [...items.map((item) => item?.toString())];
    }

    if (items.length === 0) return "";
    return items[items.length - 1]?.toString();
  };

  return (
    <Box className="ms-container" sx={{ width: "100%" }}>
      <Box className="ms-input-wrapper" ref={selectRef}>
        <Typography className="ms-floating-label" variant="body2">
          {props.Label}
        </Typography>
        <FormControl fullWidth variant="outlined" className="ms-form-control">
          <Select
            open={isOpen}
            multiple={!(props.DisableMultipleSelect ?? false)}
            value={getSelectedValue()}
            onOpen={handleOpen}
            onClose={handleClose}
            displayEmpty
            IconComponent={({ className }) =>
              TriangleIcon(className, props.Disabled)
            }
            className={`ms-select ${props.Disabled ? "ms-disabled" : ""}`}
            MenuProps={getMenuProps()}
            renderValue={(selected) => (
              <Box className="ms-selector-content">
                <Typography
                  className="ms-subtitle"
                  style={{ marginTop: "2px" }}
                  variant="body2"
                >
                  {selectedItems?.length > 0
                    ? `${selectedItems.length} ${props.Label} Selected`
                    : ""}
                </Typography>
              </Box>
            )}
          >
            {/* Controls Header */}
            {/* Select All / Deselect All Buttons */}
            {!props.DisableMultipleSelect && (
              <Stack
                direction="row"
                className="flex--container space-between ml-5 mr-5"
              >
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  sx={{
                    color: "#0277bd",
                    textTransform: "none",
                    fontSize: "14px",
                  }}
                  // Disable tab focus to prevent keyboard navigation issues
                  tabIndex={-1}
                >
                  Select All
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeselectAll();
                  }}
                  sx={{
                    color: "#0277bd",
                    textTransform: "none",
                    fontSize: "14px",
                  }}
                  // Disable tab focus to prevent keyboard navigation issues
                  tabIndex={-1}
                >
                  Deselect All
                </Button>
              </Stack>
            )}

            {/* Search Field */}
            <div className="ml-15 mr-15" style={{ marginBottom: "2px" }}>
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  border: "1px solid #e0e0e0",
                  borderRadius: "25px",
                  outline: "none",
                  "&:focus": {
                    outline: "none",
                  },
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <InputBase
                  value={searchTerm}
                  onChange={(event) => {
                    event.stopPropagation();
                    handleSearchChange(event.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleInputKeyDown} // Add this to handle keyboard events
                  sx={{ ml: 1, flex: 1 }}
                  placeholder={
                    props?.Placeholder ? props.Placeholder : "Search by Choices"
                  }
                  inputProps={{
                    "aria-label": "search",
                    // Additional input props to prevent focus loss
                    autoComplete: "off",
                    spellCheck: false,
                  }}
                  autoFocus // Auto focus the search input when menu opens
                />
                {isMouseOver && searchTerm && searchTerm.length > 0 && (
                  <Tooltip title="Clear" placement="top">
                    <IconButton
                      className="mr-0"
                      onClick={handleClearSearch}
                      tabIndex={-1} // Prevent tab focus
                    >
                      <CloseIcon
                        style={{ color: "#219ebc", fontSize: "20px" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Search" placement="top">
                  <IconButton
                    type="button"
                    sx={{ p: "10px" }}
                    aria-label="search"
                    className="m-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSearch();
                    }}
                    tabIndex={-1} // Prevent tab focus
                  >
                    <SearchNormal1
                      variant="Outline"
                      className="ico-secondary"
                      size={18}
                    />
                  </IconButton>
                </Tooltip>
              </Paper>
            </div>

            {/* Options List */}
            <Box className="ms-options-container">
              {options?.length === 0 ? (
                <Box
                  sx={{ padding: "16px", textAlign: "center", color: "#666" }}
                >
                  <Typography variant="body2">No results found</Typography>
                </Box>
              ) : (
                options?.map((option) => (
                  <MenuItem
                    disabled={option.disabled}
                    key={option.Value}
                    value={option.Value}
                    className="ms-option-item"
                    sx={{
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                    onClick={(event) => handleOptionClick(option.Value, event)}
                  >
                    <HPRCheckBox
                      Name={""}
                      checked={
                        option.disabled || selectedItems.includes(option.Value)
                      }
                      onCheckBoxChange={(name, value) => {
                        // This handler is now only triggered by direct checkbox clicks
                        // The row click is handled by handleOptionClick
                        handleCheckboxSelection(option.Value, value);
                      }}
                    />
                    <Typography className="ms-option-label">
                      {option.Label}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Box>
            <div
              style={{ width: "100%", display: "flex", justifyContent: "end" }}
            >
              <Button
                className="rem-lined-btn mr-10 "
                variant="outlined"
                style={{
                  height: "30px",
                  fontSize: "0.875rem",
                }}
                onClick={(e) => {
                  handleClose();
                }}
                tabIndex={-1}
              >
                {props.ButtonLabel}
              </Button>
            </div>
          </Select>
        </FormControl>
      </Box>
      {props.IsEnableValidator &&
        props.Validator?.message(
          props.Label,
          props.SelectedValueArray,
          "required"
        )}
    </Box>
  );
};

export default MultipleSelectorWithSearch;
